import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import { BackTo } from '@deeptrue-frontend/ui';
import queryString from 'query-string';
import { OrganizationType } from 'store/modules/auth/auth';
import { Dashboard } from 'components/Layout';
import { useFormatMessage } from 'components/IntlProvider';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import HistoryTable from './HistoryTable';
import RouterProvider from 'components/RouterProvider/RouterProvider';

const { ROUTES } = RouterProvider;
const { connect, selectors } = StoreProvider;

const StyledRootContainer = styled.section`
  flex-grow: 1;
`;

const StyledOrderHistoryBlock = styled.div`
  margin-top: 3rem;
  margin-right: 1rem;
  overflow-x: auto;
  border: 1px solid #e6e6e5;
  border-radius: 4px;
`;

interface PaymentsProps extends RouteComponentProps<any> {
  organization: OrganizationType;
}

interface Invoice {
  id: number;
  invoice_pdf: string;
}

interface PaymentsResult {
  id: number;
  created_at: string;
  invoice: Invoice;
  stripe_session_id: string;
  paid: boolean;
  email: string;
  address_info: number;
  quantity: number;
  amount: number;
  currency: string;
  type: number;
}

interface PaymentsData {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<PaymentsResult>;
}

const OrderHistory = ({ organization, location, history }: PaymentsProps) => {
  const [paymentsRes, setPaymentsRes] = useState<PaymentsData>({
    count: -1,
    next: null,
    previous: null,
    results: [],
  });
  const t = useFormatMessage();
  const parsedSearch = queryString.parse(location.search);

  useEffect(() => {
    api.resources
      .getOrganizationPayments(
        organization.codename,
        parsedSearch as {
          limit: string;
          offset: string;
          search: string;
        }
      )
      .then(({ data }) => {
        setPaymentsRes(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.codename, location.search]);

  const onChangePage = (page: number) => {
    const params = { limit: 20, offset: 20 * page - 20 };
    history.push(
      `${ROUTES.PAYMENTS.ORDER_HISTORY}?${queryString.stringify(params)}`
    );
  };

  const getPageNumber = () => {
    if (parsedSearch.offset && !Array.isArray(parsedSearch.offset)) {
      return parseInt(parsedSearch.offset, 10) / 20 + 1;
    }

    return 0;
  };

  return (
    <Dashboard title="Order History" location={location}>
      <StyledRootContainer>
        <BackTo
          onClick={() => {
            history.push(`${ROUTES.PAYMENTS.MAIN}`);
          }}
        >
          {t('pages.payment.goback', 'Back to payments')}
        </BackTo>
        <StyledOrderHistoryBlock>
          <HistoryTable
            payments={paymentsRes.results}
            fullHistory
            onChangePage={onChangePage}
            count={paymentsRes.count}
            currentPage={getPageNumber()}
          />
        </StyledOrderHistoryBlock>
      </StyledRootContainer>
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  }))
)(OrderHistory);
